import {useSalaryBracketsQuery, useSetSalaryBracketMutation} from "@irati/payrollpay/hooks"
import Flex from "@irati/ui/src/Flex"
import {Select, ValidationButton} from "@irati/ui/src/FormItem"
import Modal from "@irati/ui/src/GenericModal"
import Loading from "@irati/ui/src/Loading"
import {Body, H2} from "@irati/ui/src/Typography"
import FormattedMessage from "@irati/utils/lang"
import Error from "@irati/utils/services/apollo/Loading/Error"
import Validation from "@irati/utils/validation"
import React, {useState} from "react"
import {useTheme} from "styled-components"

interface SalaryBracketModalProps {
  show: boolean
  onSalaryBracketSet: () => void
}

export const SalaryBracketModal: React.FC<SalaryBracketModalProps> = ({show, onSalaryBracketSet}) => {
  const {data: salaryBracketsData, load: salaryBracketsLoading, error} = useSalaryBracketsQuery(null)
  const [setSalaryBracket, apollo] = useSetSalaryBracketMutation()
  const [selectedSalaryBracket, setSelectedSalaryBracket] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const theme = useTheme()

  const handleSalaryBracketChange = (value: any) => {
    setSelectedSalaryBracket(value)
    if (validationError) {
      setValidationError(null)
    }
  }

  const handleConfirm = async () => {
    if (!selectedSalaryBracket) {
      setValidationError("platform.payrollPay.salaryBracket.validation.required")
      return
    }

    try {
      await setSalaryBracket({
        variables: {
          salaryBracketId: selectedSalaryBracket,
          payrollFrequency: ""
        }
      })
      onSalaryBracketSet()
    } catch (mutationError) {
      /* no-op */
    }
  }

  return (
    <Modal
      show={show}
      critical
      small
      onConfirm={handleConfirm}
      actions={
        <>
          {apollo.error && <Error apollo={apollo} />}
          <ValidationButton large onClick={handleConfirm} primary apolloState={apollo}>
            <FormattedMessage id="platform.payrollPay.salaryBracket.continue.button" />
          </ValidationButton>
        </>
      }>
      <Flex direction="column" alignItems="center">
        <H2>
          <FormattedMessage id="platform.payrollPay.salaryBracket.title" />
        </H2>
        <Body marginTop marginBottom={3} textAlign="center" color={theme.colors.greys.grey50}>
          <FormattedMessage id="platform.payrollPay.salaryBracket.description" />
        </Body>
        {show && salaryBracketsData && (
          <Loading load={salaryBracketsLoading}>
            <Select
              id={"salary-bracket-select"}
              label={<FormattedMessage id="platform.payrollPay.salaryBracket.select.label" />}
              options={salaryBracketsData?.map((i) => ({
                label: i.description,
                value: i.id
              }))}
              value={selectedSalaryBracket}
              onChange={handleSalaryBracketChange}
              placeholder="platform.payrollPay.salaryBracket.select.placeholder"
              validation={Validation.string("platform.payrollPay.salaryBracket.selection.error")}
            />
          </Loading>
        )}
        <Body small margin={[3, 0, 3, 0]}>
          <FormattedMessage id="platform.payrollPay.salaryBracket.disclaimer" />
        </Body>
      </Flex>
    </Modal>
  )
}
